// src/components/ChatPanel.tsx
import React, { useState, useRef, useEffect } from "react";

interface ChatMessage {
  sender: string;
  message: string;
  timestamp?: Date;
}

interface ChatPanelProps {
  messages: ChatMessage[];
  onSendMessage: (message: string) => void;
  currentUsername: string;
  opponentUsername?: string;
  connected: boolean;
  disabled?: boolean;
  onChatActivationChange?: (activated: boolean) => void;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  messages,
  onSendMessage,
  currentUsername,
  opponentUsername,
  connected,
  disabled = false,
  onChatActivationChange,
}) => {
  const [inputMessage, setInputMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [chatActivated, setChatActivated] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Handle chat activation toggle
  const handleChatActivationChange = (activated: boolean) => {
    setChatActivated(activated);
    if (onChatActivationChange) {
      onChatActivationChange(activated);
    }
  };

  const handleSendMessage = () => {
    const trimmedMessage = inputMessage.trim();
    setErrorMessage(null);

    if (!trimmedMessage) return;
    if (!chatActivated) {
      setErrorMessage("Chat is not activated");
      setTimeout(() => setErrorMessage(null), 3000);
      return;
    }
    if (!connected) {
      setErrorMessage("Not connected to server");
      setTimeout(() => setErrorMessage(null), 3000);
      return;
    }
    if (trimmedMessage.length > 500) {
      setErrorMessage("Message too long (max 500 characters)");
      setTimeout(() => setErrorMessage(null), 3000);
      return;
    }

    onSendMessage(trimmedMessage);
    setInputMessage("");

    // Focus back on input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTimestamp = (timestamp?: Date): string => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div
      style={{
        backgroundColor: "#f5f5f5",
        borderRadius: "8px",
        border: "2px solid #333",
        padding: "15px",
        display: "flex",
        flexDirection: "column",
        height: "300px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
          paddingBottom: "8px",
          borderBottom: "2px solid #4CAF50",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <h3 style={{ margin: 0, fontSize: "14px", color: "#333" }}>
            💬 Game Chat
          </h3>
          {/* Chat Activation Checkbox */}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "11px",
              color: chatActivated ? "#4CAF50" : "#666",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <input
              type="checkbox"
              checked={chatActivated}
              onChange={(e) => handleChatActivationChange(e.target.checked)}
              style={{
                width: "14px",
                height: "14px",
                cursor: "pointer",
                accentColor: "#4CAF50",
              }}
            />
            <span style={{ fontWeight: chatActivated ? "bold" : "normal" }}>
              {chatActivated ? "Active" : "Enable"}
            </span>
          </label>
        </div>
        <div
          style={{
            fontSize: "10px",
            color: connected ? "#4CAF50" : "#f44336",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <span
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: connected ? "#4CAF50" : "#f44336",
            }}
          />
          {connected ? "Connected" : "Disconnected"}
        </div>
      </div>

      {/* Status Info */}
      {opponentUsername && (
        <div
          style={{
            fontSize: "11px",
            color: "#666",
            marginBottom: "8px",
            padding: "4px 8px",
            backgroundColor: "#e8f5e9",
            borderRadius: "4px",
            textAlign: "center",
          }}
        >
          Chatting with <strong>{opponentUsername}</strong>
        </div>
      )}

      {/* Messages Container */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          backgroundColor: "#fff",
          borderRadius: "4px",
          padding: "10px",
          marginBottom: "10px",
          border: "1px solid #ddd",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {!chatActivated ? (
          <div
            style={{
              textAlign: "center",
              color: "#999",
              fontStyle: "italic",
              fontSize: "12px",
              marginTop: "20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span style={{ fontSize: "24px" }}>💬</span>
            <span>Chat is not activated</span>
            <span style={{ fontSize: "10px", color: "#bbb" }}>
              Check the box above to enable chat
            </span>
          </div>
        ) : messages.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              color: "#999",
              fontStyle: "italic",
              fontSize: "12px",
              marginTop: "20px",
            }}
          >
            No messages yet. Say hello!
          </div>
        ) : (
          messages
            .filter((msg, index, arr) => {
              // If message has no timestamp, check if there's a duplicate with timestamp
              if (!msg.timestamp) {
                const hasDuplicateWithTimestamp = arr.some(
                  (other, otherIndex) =>
                    otherIndex !== index &&
                    other.message === msg.message &&
                    (other.sender === msg.sender ||
                      (other.sender === currentUsername &&
                        msg.sender === "You") ||
                      (other.sender === "You" &&
                        msg.sender === currentUsername)) &&
                    other.timestamp
                );
                // Filter out messages without timestamps if a timestamped version exists
                return !hasDuplicateWithTimestamp;
              }
              return true;
            })
            .map((msg, index) => {
              const isOwnMessage =
                msg.sender === "You" || msg.sender === currentUsername;

              return (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isOwnMessage ? "flex-end" : "flex-start",
                  }}
                >
                  <div
                    style={{
                      maxWidth: "80%",
                      padding: "8px 12px",
                      borderRadius: "12px",
                      backgroundColor: isOwnMessage ? "#4CAF50" : "#e0e0e0",
                      color: isOwnMessage ? "#fff" : "#333",
                      wordWrap: "break-word",
                      fontSize: "12px",
                      lineHeight: "1.4",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: "11px",
                        marginBottom: "2px",
                        opacity: 0.9,
                      }}
                    >
                      {isOwnMessage ? "You" : msg.sender}
                    </div>
                    <div>{msg.message}</div>
                    {msg.timestamp && (
                      <div
                        style={{
                          fontSize: "9px",
                          marginTop: "4px",
                          opacity: 0.7,
                          textAlign: "right",
                        }}
                      >
                        {formatTimestamp(msg.timestamp)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div style={{ display: "flex", gap: "8px" }}>
        <input
          ref={inputRef}
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={
            disabled
              ? "Chat disabled"
              : !chatActivated
              ? "Chat not activated"
              : connected
              ? "Type a message..."
              : "Not connected"
          }
          disabled={disabled || !chatActivated || !connected}
          maxLength={500}
          style={{
            flex: 1,
            padding: "8px 12px",
            borderRadius: "4px",
            border: "2px solid #ddd",
            fontSize: "12px",
            outline: "none",
            backgroundColor: disabled || !chatActivated || !connected ? "#f0f0f0" : "#fff",
            cursor: disabled || !chatActivated || !connected ? "not-allowed" : "text",
          }}
        />
        <button
          onClick={handleSendMessage}
          disabled={disabled || !chatActivated || !connected || !inputMessage.trim()}
          style={{
            padding: "8px 16px",
            backgroundColor:
              disabled || !chatActivated || !connected || !inputMessage.trim()
                ? "#ccc"
                : "#4CAF50",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor:
              disabled || !chatActivated || !connected || !inputMessage.trim()
                ? "not-allowed"
                : "pointer",
            fontSize: "12px",
            fontWeight: "bold",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) => {
            if (!disabled && chatActivated && connected && inputMessage.trim()) {
              e.currentTarget.style.backgroundColor = "#45a049";
            }
          }}
          onMouseLeave={(e) => {
            if (!disabled && chatActivated && connected && inputMessage.trim()) {
              e.currentTarget.style.backgroundColor = "#4CAF50";
            }
          }}
        >
          Send
        </button>
      </div>

      {/* Character Counter */}
      <div
        style={{
          fontSize: "10px",
          color: inputMessage.length > 450 ? "#f44336" : "#999",
          textAlign: "right",
          marginTop: "4px",
        }}
      >
        {inputMessage.length}/500
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div
          style={{
            marginTop: "8px",
            padding: "8px 12px",
            backgroundColor: "#ffebee",
            color: "#c62828",
            borderRadius: "4px",
            fontSize: "12px",
            textAlign: "center",
            border: "1px solid #ef9a9a",
          }}
        >
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default ChatPanel;
